import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Flower2, Mail, Lock, Eye, EyeOff, User, Phone, GraduationCap, CheckSquare, Square, ShieldCheck, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usePhone, setUsePhone] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    school: '',
    password: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.password) {
      toast({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs obligatoires',
        variant: 'destructive'
      });
      return;
    }

    if (usePhone && !formData.phone) {
      toast({
        title: 'Erreur',
        description: 'Veuillez entrer votre numéro de téléphone',
        variant: 'destructive'
      });
      return;
    }

    if (!usePhone && !formData.email) {
      toast({
        title: 'Erreur',
        description: 'Veuillez entrer votre email',
        variant: 'destructive'
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Erreur',
        description: 'Les mots de passe ne correspondent pas',
        variant: 'destructive'
      });
      return;
    }

    if (!acceptedTerms) {
      toast({
        title: 'Erreur',
        description: 'Vous devez accepter les conditions d\'utilisation',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    
    // Merge onboarding data if available
    let onboardingData = {};
    try {
      const stored = localStorage.getItem('onboarding_data');
      if (stored) onboardingData = JSON.parse(stored);
    } catch (e) {}

    const userData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      password: formData.password,
      school: formData.school || null,
      ...(usePhone ? { phone: formData.phone } : { email: formData.email }),
      onboarding: onboardingData
    };

    const result = await register(userData);
    setLoading(false);

    if (result.success) {
      localStorage.removeItem('onboarding_data');
      if (result.recoveryCode) {
        // Phone user - show recovery code dialog
        setRecoveryCode(result.recoveryCode);
      } else {
        toast({
          title: 'Inscription réussie!',
          description: 'Bienvenue dans la communauté Fleur de Vie'
        });
        navigate('/dashboard');
      }
    } else {
      toast({
        title: 'Erreur d\'inscription',
        description: result.error,
        variant: 'destructive'
      });
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast({ title: 'Code', description: recoveryCode });
    }
  };

  const handleCloseRecovery = () => {
    setRecoveryCode(null);
    toast({
      title: 'Inscription réussie!',
      description: 'Bienvenue dans la communauté Fleur de Vie'
    });
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md p-8 bg-white/10 backdrop-blur-md border-white/20">
        <div className="text-center mb-8">
          <Flower2 className="w-16 h-16 text-pink-300 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Fleur de Vie</h1>
          <h2 className="text-xl font-semibold text-white mb-2">Inscription</h2>
          <p className="text-pink-200 text-sm">Créez votre compte gratuit</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-white text-sm mb-2 block">Prénom *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-pink-300" />
              <Input
                type="text"
                placeholder="Votre prénom"
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 h-12"
              />
            </div>
          </div>

          <div>
            <label className="text-white text-sm mb-2 block">Nom *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-pink-300" />
              <Input
                type="text"
                placeholder="Votre nom"
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 h-12"
                data-testid="register-lastname"
              />
            </div>
          </div>

          <div>
            <label className="text-white text-sm mb-2 block">Établissement scolaire <span className="text-pink-300">(optionnel)</span></label>
            <div className="relative">
              <GraduationCap className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-pink-300" />
              <Input
                type="text"
                placeholder="Nom de votre école ou établissement"
                value={formData.school}
                onChange={(e) => setFormData({...formData, school: e.target.value})}
                className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 h-12"
                data-testid="register-school"
              />
            </div>
            <p className="text-pink-300/70 text-xs mt-1">Pour participer au classement des établissements</p>
          </div>

          {/* Toggle between Email and Phone */}
          <div className="flex gap-2 mb-2">
            <Button
              type="button"
              onClick={() => setUsePhone(false)}
              className={`flex-1 ${!usePhone ? 'bg-pink-500' : 'bg-white/10'} hover:bg-pink-600 text-white`}
            >
              <Mail className="w-4 h-4 mr-2" />
              Email
            </Button>
            <Button
              type="button"
              onClick={() => setUsePhone(true)}
              className={`flex-1 ${usePhone ? 'bg-pink-500' : 'bg-white/10'} hover:bg-pink-600 text-white`}
            >
              <Phone className="w-4 h-4 mr-2" />
              Téléphone
            </Button>
          </div>

          {usePhone ? (
            <div>
              <label className="text-white text-sm mb-2 block">Numéro de téléphone *</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-pink-300" />
                <Input
                  type="tel"
                  placeholder="+225 XX XX XX XX XX"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 h-12"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="text-white text-sm mb-2 block">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-pink-300" />
                <Input
                  type="email"
                  placeholder="votre.email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 h-12"
                />
              </div>
            </div>
          )}

          <div>
            <label className="text-white text-sm mb-2 block">Mot de passe *</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-pink-300" />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Créer un mot de passe"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="pl-12 pr-12 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 h-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-pink-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-white text-sm mb-2 block">Confirmer le mot de passe *</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-pink-300" />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirmer le mot de passe"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 h-12"
              />
            </div>
          </div>

          {/* Terms Acceptance */}
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setAcceptedTerms(!acceptedTerms)}
              className="flex items-start gap-3 w-full text-left"
              data-testid="accept-terms-checkbox"
            >
              {acceptedTerms ? (
                <CheckSquare className="w-5 h-5 text-pink-400 mt-0.5 flex-shrink-0" />
              ) : (
                <Square className="w-5 h-5 text-pink-300 mt-0.5 flex-shrink-0" />
              )}
              <span className="text-pink-200 text-sm leading-relaxed">
                J'accepte les{' '}
                <Link 
                  to="/terms" 
                  className="text-pink-300 underline hover:text-pink-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  conditions d'utilisation
                </Link>
                {' '}et la{' '}
                <Link 
                  to="/privacy" 
                  className="text-pink-300 underline hover:text-pink-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  politique de confidentialité
                </Link>
              </span>
            </button>
          </div>

          <Button 
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white font-semibold py-6 rounded-xl disabled:opacity-50"
          >
            {loading ? 'Inscription...' : 'Créer mon compte'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-pink-200 text-sm">
            Déjà un compte ?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-pink-300 font-semibold hover:underline"
            >
              Se connecter
            </button>
          </p>
        </div>
      </Card>

      {/* Recovery Code Dialog */}
      <Dialog open={!!recoveryCode} onOpenChange={() => {}}>
        <DialogContent className="bg-purple-900 border-purple-700 max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-green-400" />
              Code de récupération
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-pink-200 text-sm">
              Notez ce code en lieu sûr. Il vous permettra de <strong className="text-white">réinitialiser votre mot de passe</strong> si vous l'oubliez.
            </p>
            
            <div className="bg-white/10 rounded-xl p-4 text-center border border-pink-400/30">
              <p className="text-3xl font-mono font-bold text-white tracking-wider" data-testid="recovery-code">
                {recoveryCode}
              </p>
            </div>

            <Button
              onClick={handleCopyCode}
              variant="outline"
              className="w-full border-pink-400/30 text-pink-200 hover:bg-white/10"
              data-testid="copy-recovery-code-btn"
            >
              {copied ? <Check className="w-4 h-4 mr-2 text-green-400" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Copié !' : 'Copier le code'}
            </Button>

            <div className="bg-orange-500/20 border border-orange-400/30 rounded-lg p-3">
              <p className="text-orange-200 text-xs">
                Ce code ne sera plus affiché. Si vous le perdez, vous devrez contacter le support pour réinitialiser votre mot de passe.
              </p>
            </div>

            <Button
              onClick={handleCloseRecovery}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
              data-testid="confirm-saved-code-btn"
            >
              J'ai noté mon code, continuer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Register;
